import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { GeneratedCover } from '@/components/common/Primitives';
import { adService } from '@/services/ads';

const ROTATE_MS = 6000;
const CACHE_MS = 120000;

// Cache simples: evita buscar os banners a cada perfil aberto na mesma visita
let cache = { at: 0, data: null, promise: null };

function loadBanners() {
  if (cache.data && Date.now() - cache.at < CACHE_MS) return Promise.resolve(cache.data);
  if (!cache.promise) {
    cache.promise = adService
      .activeBanners()
      .then((d) => {
        cache = { at: Date.now(), data: d || [], promise: null };
        return cache.data;
      })
      .catch(() => {
        cache.promise = null;
        return [];
      });
  }
  return cache.promise;
}

export function AdCover({ seed, children }) {
  const [banners, setBanners] = useState(cache.data || []);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const viewed = useRef(new Set());

  useEffect(() => {
    let active = true;
    loadBanners().then((list) => {
      if (active) setBanners(list);
    });
    return () => {
      active = false;
    };
  }, []);

  const idx = banners.length ? index % banners.length : 0;
  const current = banners[idx];

  // Conta uma visualização por banner a cada vez que ele aparece
  useEffect(() => {
    if (!current || viewed.current.has(current.campaign_id)) return;
    viewed.current.add(current.campaign_id);
    adService.track(current.campaign_id, 'view');
  }, [current?.campaign_id]);

  // Troca automática
  useEffect(() => {
    if (banners.length < 2 || paused) return undefined;
    const t = setInterval(() => setIndex((x) => (x + 1) % banners.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [banners.length, paused]);

  if (!current) {
    return (
      <GeneratedCover seed={seed} className="h-44 border-b border-white/10 sm:h-56">
        {children}
      </GeneratedCover>
    );
  }

  const go = (n) => setIndex((n + banners.length) % banners.length);

  return (
    <div
      className="relative h-44 overflow-hidden border-b border-white/10 bg-[#090d14] sm:h-56"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="ad-cover"
    >
      <a
        href={current.target_url}
        target="_blank"
        rel="noopener noreferrer nofollow"
        onClick={() => adService.track(current.campaign_id, 'click')}
        className="absolute inset-0 block"
        aria-label={`Anúncio de ${current.company_name}`}
      >
        <img src={current.image_url} alt={`Anúncio de ${current.company_name}`} className="h-full w-full object-cover" />
      </a>

      <span className="pointer-events-none absolute bottom-2 right-3 z-10 rounded bg-black/60 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        Patrocinado
      </span>

      {/* Camada de cima: botões do perfil (Diretório etc.) e setas continuam clicáveis */}
      <div className="pointer-events-none absolute inset-0 z-20 [&_a]:pointer-events-auto [&_button]:pointer-events-auto">
        {children}
        {banners.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(idx - 1)}
              aria-label="Anúncio anterior"
              className="absolute left-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full bg-black/50 text-white hover:bg-black/70"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => go(idx + 1)}
              aria-label="Próximo anúncio"
              className="absolute right-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full bg-black/50 text-white hover:bg-black/70"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1.5">
              {banners.map((b, n) => (
                <button
                  key={b.campaign_id}
                  type="button"
                  onClick={() => setIndex(n)}
                  aria-label={`Ir para o anúncio ${n + 1}`}
                  className={`h-1.5 rounded-full transition-all ${n === idx ? 'w-5 bg-white' : 'w-1.5 bg-white/50'}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}